import { BaseService } from '@common/http-layer/service/base.service'
import { readAmbientNuxtApp } from '@common/core-layer/utils/ambient-nuxt-app'
import { useLocationStore } from '@common/location-layer/store/location.store'
import type { IProductDetail, ProductValidity } from '@courses/types-layer/types/product'
import type { Pinia } from 'pinia'

export class ProductService extends BaseService {
  constructor() {
    super({
      baseURL: 'MEMBERSHIPS_SERVER_URL',
      basePath: '',
    })
  }

  /**
   * Only reached when a caller omits `locationId`. Reads the location store directly
   * instead of `useLocation()` and guards it behind an ambient-Pinia check — on SSR,
   * outside an active Nuxt/Pinia context (e.g. the courses-access-guard middleware's
   * async query fetchFn), `useLocationStore()` throws reading `pinia._s`. Mirrors the
   * same guard `BaseService.prepareRequest` already applies before reading the auth store.
   */
  private resolveFallbackLocationId(): string | undefined {
    const ambientPinia = readAmbientNuxtApp()?.$pinia as Pinia | undefined
    if (!ambientPinia) {
      this.logger.warn('Not in Nuxt context, skipping location store read', {
        labels: { context: 'ProductService.endpoint' },
      })
      return undefined
    }
    return useLocationStore(ambientPinia).locationId || undefined
  }

  private endpoint(path: string = '', locationId?: string): string {
    const resolvedLocationId = locationId || this.resolveFallbackLocationId()
    if (!resolvedLocationId) throw new Error('Location ID is required')
    return `/locations/${resolvedLocationId}/products${path}`
  }

  async pinProduct(productId: string) {
    const { data } = await this.post(this.endpoint(`/${productId}/pin`))
    return data
  }

  async unpinProduct(productId: string) {
    const { data } = await this.post(this.endpoint(`/${productId}/unpin`))
    return data
  }

  async pinnedProducts() {
    const { data } = await this.get<IProductDetail[]>(this.endpoint(`/pinned`))
    return data
  }

  async findById(id: string): Promise<IProductDetail> {
    const { data } = await this.get<IProductDetail>(this.endpoint(`/${id}`))
    return data
  }

  /**
   * Learner's access window. The product endpoint answers 403 `{ hasProductAccess: false, validityStart, validityEnd }`
   * when access is closed; that payload is returned as data. No dates means there is no purchase (e.g. revoked).
   */
  async getValidity(id: string, locationId?: string): Promise<ProductValidity> {
    try {
      await this.get<IProductDetail>(this.endpoint(`/${id}`, locationId))
      return { hasProductAccess: true, validityStart: null, validityEnd: null }
    } catch (error) {
      const { status, data } = (error ?? {}) as { status?: number; data?: Partial<ProductValidity> }
      if (status !== 403 || data?.hasProductAccess !== false) throw error
      return { hasProductAccess: false, validityStart: data.validityStart ?? null, validityEnd: data.validityEnd ?? null }
    }
  }

  async getCustomizationSettings(productId: string, locationId: string, params?: Record<string, unknown>) {
    const { data } = await this.get(this.endpoint(`/product-themes/${productId}`), {
      params,
      locationId,
      productId,
    })
    return data
  }

  async getProductTemplateByProductId(productId: string, locationId: string) {
    const { data } = await this.get(this.endpoint(`/themes-v2/${productId}`, locationId), {
      locationId,
      productId,
    })
    return data
  }

  async checkIfProductThemeExists(productId: string, locationId: string) {
    const { data } = await this.get(this.endpoint(`/themes-v2/${productId}/check-exists`, locationId), {
      locationId,
      productId,
    })
    return data
  }

  async getProductTemplateById(id: string, productId: string, locationId: string) {
    const { data } = await this.get(this.endpoint(`/themes-v2/${productId}/theme/${id}`, locationId), {
      locationId,
      productId,
    })
    return data
  }

  async getTemplateById(id: string, productId: string, locationId: string) {
    const { data } = await this.get(this.endpoint(`/themes-v2/templates/${id}`, locationId), {
      locationId,
      productId,
    })
    return data
  }
}
